import { Group } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGroups } from "@/hooks/groups/useGroups";
import { DiagnosticView } from "@/models/DiagnosticView";

interface Props {
  open: boolean;
  initial?: Partial<DiagnosticView>;
  title: string;
  onClose: () => void;
  onSubmit: (meta: { name: string; description?: string; groups: Group[] }) => void;
}

export function ViewMetadataDialog({ open, initial, title, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const { data: allGroups = [] } = useGroups();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setGroups(initial?.groups ?? []);
    }
  }, [open, initial]);

  const canSubmit = name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t("views.dialog.name")}
            value={name}
            onChange={e => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t("views.dialog.description")}
            value={description}
            onChange={e => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
          <Autocomplete
            multiple
            options={allGroups}
            value={groups}
            onChange={(_, v) => setGroups(v)}
            getOptionLabel={o => o.name ?? o.id ?? ""}
            getOptionKey={o => o.id ?? ""}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name ?? option.id}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key: _key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    {...tagProps}
                    key={option.id ?? `idx-${index}`}
                    size="small"
                    label={option.name ?? option.id}
                  />
                );
              })
            }
            renderInput={p => <TextField {...p} label={t("views.dialog.groups")} />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({ name: name.trim(), description: description.trim() || undefined, groups })
          }
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
